import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { ChartScalesType, TabValueType, TabValues } from "../components/Enums";
import { Patient } from "../models/patient";
import { PatientData } from "../models/patient-data";

const unavailable = () => {
  throw new Error("AppContext not available");
};

export const AppContext = createContext<{
  tab: TabValueType;
  chartConfig: { scale?: ChartScalesType };
  dataConfig: {
    patientId?: Patient["id"] | null;
    exportId?: PatientData["exportId"] | null;
  };
  patientDialog: {
    open: boolean;
    patient?: Patient | null;
  };
  patientMenu?: {
    anchorEl?: HTMLElement | null;
    displayImports?: boolean;
    patient?: Patient | null;
  };
  legend: {
    focused?: null | string;
    hidden?: null | string[];
  };
  setTab: Dispatch<SetStateAction<TabValueType>>;
  setChartConfig: Dispatch<SetStateAction<{ scale?: ChartScalesType }>>;
  setDataConfig: Dispatch<
    SetStateAction<{
      patientId?: Patient["id"] | null;
      exportId?: PatientData["exportId"] | null;
    }>
  >;
  setLegend: Dispatch<SetStateAction<{ hidden?: null | string[] }>>;
  settingsDialog: boolean;
  deleteExportDialog: {
    open: boolean;
    id: string | null;
    date: string | null;
  };
  setDeleteExportDialog: Dispatch<
    SetStateAction<{
      open: boolean;
      id: string | null;
      date: string | null;
    }>
  >;
  timelinePopover?: {
    anchorEl?: HTMLElement | null;
    enforceOpen?: boolean;
    marks?: { color: string; name: string; value: number; id: string }[] | null;
  };
  togglePatientDialog: (options?: {
    open?: boolean;
    patient?: Patient | null;
  }) => void;
  togglePatientMenu: (options?: {
    anchorEl: HTMLElement;
    displayImports?: boolean;
    patient?: Patient;
  }) => void;
  toggleSettingsDialog: () => void;
  toggleTimelinePopover: (options?: {
    anchorEl: HTMLElement | null;
    enforceOpen?: boolean;
    marks?: { color: string; name: string; value: number; id: string }[];
  }) => void;
}>({
  chartConfig: { scale: "time" },
  dataConfig: { patientId: undefined, exportId: undefined },
  legend: { hidden: null },
  patientDialog: { open: false, patient: undefined },
  patientMenu: { anchorEl: null, displayImports: false, patient: undefined },
  setChartConfig: unavailable,
  setDataConfig: unavailable,
  setLegend: unavailable,
  setTab: unavailable,
  settingsDialog: false,
  deleteExportDialog: { open: false, id: null, date: null },
  setDeleteExportDialog: unavailable,
  tab: TabValues.chart,
  timelinePopover: { anchorEl: null, marks: undefined },
  togglePatientDialog: unavailable,
  togglePatientMenu: unavailable,
  toggleSettingsDialog: unavailable,
  toggleTimelinePopover: unavailable,
});

export const useAppContext = () => useContext(AppContext);

const AppContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [tab, setTab] = useState<TabValueType>(TabValues.chart);

  const [legend, setLegend] = useState<{
    hidden?: string[] | null;
  }>({ hidden: null });

  const [dataConfig, setDataConfig] = useState<{
    patientId?: Patient["id"] | null;
    exportId?: PatientData["exportId"] | null;
  }>({ patientId: null, exportId: null });

  const [chartConfig, setChartConfig] = useState<{
    scale?: ChartScalesType;
  }>({
    scale: "time",
  });

  const [settingsDialog, setSettingsDialog] = useState<boolean>(false);
  const toggleSettingsDialog = () => setSettingsDialog((s) => !s);

  const [deleteExportDialog, setDeleteExportDialog] = useState<{
    open: boolean;
    id: string | null;
    date: string | null;
  }>({ open: false, id: null, date: null });

  const [patientMenu, setPatientMenu] = useState<{
    anchorEl?: HTMLElement | null;
    displayImports?: boolean;
    patient?: Patient | null;
  }>({ anchorEl: undefined, patient: undefined });

  const [timelinePopover, setTimelinePopover] = useState<{
    anchorEl?: HTMLElement | null;
    enforceOpen?: boolean;
    marks?: { color: string; name: string; value: number; id: string }[] | null;
  }>({ anchorEl: undefined, enforceOpen: false, marks: undefined });

  const [patientDialog, setPatientDialog] = useState<{
    open: boolean;
    patient?: Patient | null;
  }>({ open: false, patient: undefined });

  const togglePatientMenu = (options?: {
    anchorEl?: HTMLElement;
    displayImports?: boolean;
    patient?: Patient | null;
  }) => {
    setPatientMenu({
      anchorEl: options?.patient ? options.anchorEl : null,
      displayImports: options?.displayImports,
      patient: options?.patient,
    });
  };

  const toggleTimelinePopover = (options?: {
    anchorEl?: HTMLElement | null;
    enforceOpen?: boolean;
    marks?: { color: string; name: string; value: number; id: string }[] | null;
  }) => {
    setTimelinePopover({
      anchorEl: options?.marks ? options.anchorEl : null,
      enforceOpen: options?.enforceOpen,
      marks: options?.marks,
    });
  };

  const togglePatientDialog = (options?: {
    open?: boolean;
    patient?: Patient | null;
  }) => {
    setPatientDialog((s) => ({
      open: options?.open || Boolean(options?.patient) || !s.open,
      patient: options?.patient,
    }));
  };

  useEffect(() => {
    if (!patientMenu.anchorEl)
      setPatientMenu((s) => ({ ...s, patient: null, displayImports: false }));
  }, [setPatientMenu, patientMenu.anchorEl]);

  useEffect(() => {
    if (!patientDialog.open) setPatientDialog((s) => ({ ...s, patient: null }));
  }, [setPatientDialog, patientDialog.open]);

  useEffect(() => {
    if (!timelinePopover.anchorEl)
      setTimelinePopover((s) => ({ ...s, patient: null, enforceOpen: false }));
  }, [setTimelinePopover, timelinePopover.anchorEl]);

  useEffect(() => {
    setLegend({ hidden: null });
  }, [dataConfig.exportId, dataConfig.patientId, setLegend]);

  return (
    <AppContext.Provider
      value={{
        // legend for selecting legend in charts (timeline and linechart)
        legend,
        setLegend,

        // tab
        tab,
        setTab,

        // data config
        dataConfig,
        setDataConfig,

        // chart config
        chartConfig,
        setChartConfig,

        // menu
        patientMenu,
        togglePatientMenu,

        // dialog
        patientDialog,
        togglePatientDialog,

        // settings
        settingsDialog,
        toggleSettingsDialog,

        // delete export
        deleteExportDialog,
        setDeleteExportDialog,

        // timeline popover
        timelinePopover,
        toggleTimelinePopover,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
